import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';

/**
 * The proportion every logo box keeps, on every surface. 2:1 is the plate the current `www` site
 * uses (`h-16 w-28` with `p-2`, so 96x48 inside), and porting it is what TEC-3821 B3 asks for.
 */
const BOX_ASPECT = 2;

/**
 * The four box sizes the site draws a partner logo at, smallest first. Static class names rather than
 * an arbitrary pixel prop, so Tailwind can see them; `height` is the same number, for the CDN request.
 */
const SIZES = {
	sm: { box: 'h-10 max-w-20', image: 'max-h-10', height: 40 },
	md: { box: 'h-12 max-w-24', image: 'max-h-12', height: 48 },
	lg: { box: 'h-16 max-w-32', image: 'max-h-16', height: 64 },
	xl: { box: 'h-20 max-w-40', image: 'max-h-20', height: 80 },
} as const;

export type PartnerLogoSize = keyof typeof SIZES;

/**
 * A partner logo, normalised.
 *
 * Logos come from merchant and NGO records, so their artwork shares no size, aspect or padding — and
 * `object-contain` in a box whose aspect differs from the artwork's is what made them render at wildly
 * different sizes across the site. One box proportion everywhere is the rule: the widest lockup and
 * the squarest mark are bounded identically, and a surface chooses only how big that box is.
 *
 * It cannot make a square mark cover the same AREA as a wide lockup — `contain` fits to whichever axis
 * runs out first — so the worst source files still need replacing in the backoffice. That half of the
 * item is Comms'.
 *
 * Greyscale by default, because the artwork's transparency is unknown: `brightness-0` would paint a
 * whole opaque plate black instead of just the mark.
 */
export function PartnerLogo(props: {
	src: string | undefined;
	alt: string;
	size: PartnerLogoSize;
	/** Drop the greyscale — the visitor has singled this partner out. */
	colour?: boolean;
	/** Fetch with the page rather than on first sight — for a logo that arrives on a moving belt. */
	eager?: boolean;
	className?: string;
}) {
	const size = SIZES[props.size];
	return (
		// The filter sits on the BOX, not on the image: a caller that wants a CSS-driven state
		// (`group-hover:grayscale-0`) can then put it on the same element, where tailwind-merge can
		// resolve the pair instead of leaving both rules in the stylesheet.
		<span
			className={cn(
				'flex w-full items-center justify-center transition-[filter] duration-300',
				size.box,
				!props.colour && 'grayscale',
				props.className,
			)}
		>
			<Image
				src={props.src}
				alt={props.alt}
				height={size.height}
				aspectRatio={BOX_ASPECT}
				objectFit="contain"
				eager={props.eager}
				className={cn('w-full object-contain!', size.image)}
			/>
		</span>
	);
}
